import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getPersonaStatus,
  setPersonaStatus,
} from "../../helper/Controllers/StatusController";
import { readFromFBDB } from "../../services/fbdb.data";
import {
  fetchUserAccountInfo,
  SelectUserAccountInfo,
  selectLogInUsername,
  setCheckoutLabel,
} from "../../common/authenticateDashboard/authSlice";
import { ref, set } from "firebase/database";
import { db } from "../../firebase";
import handleToast from "../../helper/Toast";
import { ToastContainer } from "react-toastify";
import { getDataFromMongoDb, updateDataInMongoDb } from "../../services/mongod.data";
import ExportClientReports from "./ExportClientReports";
import axios from "axios";

const API_ENDPOINT =
  "https://us-central1-causalfunnel-21.cloudfunctions.net/CFDashboardUpdateWebhook";

const API_BASE_URL ="https://server.causalfunnels.com";

const extractSpreadsheetId = (inputValue) => {
  if (!inputValue) return "";

  const trimmedValue = inputValue.trim();
  const urlMatch = trimmedValue.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);

  if (urlMatch?.[1]) {
    return urlMatch[1];
  }

  return trimmedValue;
};

// Helpers and formatters moved to ExportClientReports.jsx

const Settings = () => {
  const [enable, setEnable] = useState(true);
  const [email, setEmail] = useState("");
  const [progress, setProgress] = useState(0);
  const [updateLimitReached, setUpdateLimitReached] = useState(false);
  const [updateHistory, setUpdateHistory] = useState([]);
  const sessionUser = useSelector(selectLogInUsername);
  const UserAccountInfo = useSelector(SelectUserAccountInfo);
  const user = sessionUser?.split("@")[0];
  const [currentUpdates, setCurrentUpdates] = useState(0);
  const [loading, setLoading] = useState(false); // Add loading state
  const dispatch = useDispatch();

  const [agentEnabled, setAgentEnabled] = useState(false);
  const [agentLoading, setAgentLoading] = useState(true);
  const [agentToggling, setAgentToggling] = useState(false);

  // Admin check — same as BlogUpdate.jsx
  const userType = sessionStorage.getItem("userType");
  const isAdmin = userType === "admin";

  const AGENT_API_URL = process.env.REACT_APP_AGENT_API_URL || "https://agent.causalfunnels.com/api/agent";

  // Admin Detail State
  const [adminDetail, setAdminDetail] = useState(null);
  const [adminForm, setAdminForm] = useState({
    name: "",
    emails: "",
    websiteUrl: "",
    spreadsheetId: "",
    blogFolderUrl: "",
  });
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSaving, setAdminSaving] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);

  // Fetch Admin Detail
  const fetchAdminDetail = async () => {
    if (!user) return;
    setAdminLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin-detail?collectionName=${user}&graphName=admin-detail`
      );
      if (response.ok) {
        const result = await response.json();
        // API returns { success, data: [...] } structure
        if (result.success && result.data && result.data.length > 0) {
          const adminData = result.data[0];
          setAdminDetail(adminData);
          setAdminForm({
            name: adminData.name || "",
            emails: adminData.emails || "",
            websiteUrl: adminData.websiteUrl || "",
            spreadsheetId: adminData.spreadsheetId || "",
            blogFolderUrl: adminData.blogFolderUrl || "",
          });
        } else {
          setAdminDetail(null);
          setAdminForm((prev) => ({
            ...prev,
            spreadsheetId: "",
            blogFolderUrl: "",
          }));
        }
      } else {
        setAdminDetail(null);
        setAdminForm((prev) => ({
          ...prev,
          spreadsheetId: "",
          blogFolderUrl: "",
        }));
      }
    } catch (error) {
      console.error("Error fetching admin detail:", error);
      setAdminDetail(null);
    } finally {
      setAdminLoading(false);
    }
  };

  // Save Admin Detail
  const saveAdminDetail = async () => {
    if (!user) return;
    setAdminSaving(true);
    try {
      const normalizedSpreadsheetId = extractSpreadsheetId(adminForm.spreadsheetId);
      const payload = {
        collectionName: user,
        graphName: "admin-detail",
        name: adminForm.name,
        emails: adminForm.emails,
        websiteUrl: adminForm.websiteUrl,
        spreadsheetId: normalizedSpreadsheetId,
        blogFolderUrl: adminForm.blogFolderUrl,
      };

      const hasAdminDetails =
        Boolean(adminForm.name?.trim()) ||
        Boolean(adminForm.emails?.trim()) ||
        Boolean(adminForm.websiteUrl?.trim()) ||
        Boolean(adminForm.blogFolderUrl?.trim()) ||
        Boolean(normalizedSpreadsheetId) ||
        Boolean(adminDetail?._id);

      let adminSaveOk = true;
      if (hasAdminDetails) {
        let response;
        if (adminDetail && adminDetail._id) {
          // Update existing
          response = await fetch(`${API_BASE_URL}/api/admin-detail/${adminDetail._id}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
        } else {
          // Create new
          response = await fetch(`${API_BASE_URL}/api/admin-detail`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
        }
        adminSaveOk = response.ok;
      }

      if (adminSaveOk) {
        handleToast("success", "Settings saved successfully!");
      } else {
        handleToast("error", "Failed to save admin detail");
      }

      await fetchAdminDetail();
      dispatch(fetchUserAccountInfo(user));
    } catch (error) {
      console.error("Error saving admin detail:", error);
      handleToast("error", "Error saving admin detail");
    } finally {
      setAdminSaving(false);
    }
  };

  const handleAdminFormChange = (e) => {
    const { name, value } = e.target;
    setAdminForm((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    fetchAdminDetail();
  }, [user]);

  useEffect(() => {
    const checkAgentStatus = async () => {
      if (!user) return;
      try {
        const res = await axios.get(`${AGENT_API_URL}/enabled/${user}`);
        setAgentEnabled(res.data.agentEnabled || false);
      } catch {
        setAgentEnabled(false);
      } finally {
        setAgentLoading(false);
      }
    };
    checkAgentStatus();
  }, [user]);

  const handleAgentToggle = async () => {
    setAgentToggling(true);
    try {
      const res = await axios.post(`${AGENT_API_URL}/toggle`, {
        collectionName: user,
        enabled: !agentEnabled,
        adminEmail: email || "admin",
      });
      if (res.data.success) {
        setAgentEnabled(!agentEnabled);
        handleToast("success", `AI Agent ${!agentEnabled ? "enabled" : "disabled"} for ${user}`);
      }
    } catch (err) {
      handleToast("error", "Failed to toggle AI Agent");
    } finally {
      setAgentToggling(false);
    }
  };

  const handlePersonaStatusChange = async (status) => {
    try {
      await setPersonaStatus(status);
    } catch {
      console.error("Error in handlePersonaStatusChange");
    }
  };

  useEffect(() => {
    handlePersonaStatusChange(enable);
  }, [enable]);

  useEffect(() => {
    const fetchPersonaStatus = async () => {
      try {
        const result = await getPersonaStatus();
        // console.log("result", result);
        // setPaidStatus(result);
      } catch {
        console.error("Error in fetchPersonaStatus");
      }
    };

    fetchPersonaStatus();

    const getCookie = (name) => {
      const cookies = document.cookie.split("; ");
      for (const cookie of cookies) {
        const [cookieName, cookieValue] = cookie.split("=");
        if (cookieName === name) {
          return cookieValue;
        }
      }
      return null;
    };

    const emailValue = getCookie("user");
    setEmail(emailValue);
  }, []);

  const getUnixTime = (date) => Math.floor(date.getTime() / 1000);

  const getMonday = (d) => {
    d = new Date(d);
    const day = d.getDay(),
      diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust if Sunday
    return new Date(d.setDate(diff));
  };

  useEffect(() => {
    const checkProgress = async () => {
      const currentDate = new Date();
      const monday = getMonday(currentDate);
      const year = monday.getFullYear().toString();
      const month = (monday.getMonth() + 1).toString().padStart(2, "0");
      const date = monday.getDate().toString().padStart(2, "0");
      // const weekSubpath = `${user}/graph-dashboard-update/${year}/${month}/${date}`;

      try {
        // const weeklyResult = await readFromFBDB(weekSubpath);
        const weeklyDataMongo = await getDataFromMongoDb(user, "graph-dashboard-update");
        const weekData = weeklyDataMongo.data.year.month.date || [];
        const weekDataLength = weekData.length;
        const weekLastUpdateData = weekData[weekDataLength - 1];

        // console.log("weekdata", weeklyDataMongo);

        if (weekLastUpdateData) {
          const givenTimestamp = parseInt(weekLastUpdateData.timestamp, 10);
          const startDateTime = givenTimestamp;
          const endDateTime = startDateTime + 6 * 60 * 60;
          const currentDateTime = Math.floor(Date.now() / 1000);

          // console.log("currentDateTime", currentDateTime, startDateTime);
          // console.log("endDateTime", endDateTime);

          if (currentDateTime >= startDateTime && currentDateTime <= endDateTime) {
            setUpdateLimitReached(true);
            // console.log("setting updateLimitReached", true);
          } else {
            setUpdateLimitReached(false);
            // console.log("setting updateLimitReached", false);
          }
        }

        setCurrentUpdates(weekDataLength);

        // const monthSubpath = `${user}/graph-dashboard-update/${year}/${month}`;
        // const monthlyResult = await readFromFBDB(monthSubpath);
        const monthData = await getDataFromMongoDb(user, "graph-dashboard-update");
        const monthlyResult = monthData[year][month];
        // console.log("monthlyResult", monthlyResult);
        

        if (monthlyResult.data) {
          const history = Object.values(monthlyResult.data).flat();
          history.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);

            if (dateA > dateB) return -1;
            if (dateA < dateB) return 1;

            const timestampA = parseInt(a.timestamp, 10);
            const timestampB = parseInt(b.timestamp, 10);

            return timestampB - timestampA; // For descending order
          });

          setUpdateHistory(history);
        } else {
          setUpdateHistory([]);
        }
      } catch (error) {
        console.error("Error checking progress:", error.message);
        setProgress(0);
        setUpdateLimitReached(false);
        setUpdateHistory([]);
      }
    };

    if (enable) {
      checkProgress();
    }
  }, [user, enable]);

  const makeApiCall = async (prod, startDate, endDate, emails) => {
    const url = "https://us-central1-causalfunnel-21.cloudfunctions.net/cfDashboardDataUpdate";
    const body = {
      prod,
      startDate,
      endDate,
      emails,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const message = (data && data.message) || `HTTP error ${response.status}`;
        return { message, status: data?.status || "error" };
      }

      return { message: data?.message || "Success", status: data?.status || "success" };
    } catch (error) {
      console.error("Error making the API call:", error);
      return { message: error.message || "Network error", status: "error" };
    }
  };

  const updateProgress = async () => {
    console.log(updateLimitReached);

    if (updateLimitReached) {
      handleToast("error", "Update limit for the week has been reached.");
      return;
    }

    setLoading(true); // Start loading spinner

    const currentDate = new Date();
    const monday = getMonday(currentDate);
    const clientName = user.replace("-prod", "");
    const formatYmd = (date) => date.toISOString().slice(0, 10).replace(/-/g, "");
    const startDate = formatYmd(monday);
    const endDate = formatYmd(currentDate);

    try {
      const year = monday.getFullYear().toString();
      const month = (monday.getMonth() + 1).toString().padStart(2, "0");
      const date = monday.getDate().toString().padStart(2, "0");
      const weekSubpath = `${user}/graph-dashboard-update/${year}/${month}/${date}`;
      // call new API with emails (use adminForm.emails if present else "N/A")
      const emails = adminForm?.emails && adminForm.emails.trim() ? adminForm.emails : "N/A";
      const apiResult = await makeApiCall(clientName, startDate, endDate, emails);

      // show response message in UI and toast
      if (apiResult && apiResult.message) {
        setApiResponse(apiResult);
        handleToast(apiResult.status === "success" ? "success" : "error", apiResult.message);
      }

       // read existing week data from Firebase Realtime DB instead of MongoDB
       let weekData = [];
       try {
         const fbResult = await readFromFBDB(weekSubpath).catch(() => null);
         weekData = fbResult || [];
       } catch (e) {
         weekData = [];
       }

      const givenTimestamp = Math.floor(Date.now() / 1000);
      const startDateTime = givenTimestamp - 60 * 60;
      const endDateTime = givenTimestamp + 6 * 60 * 60;
      const currentDateTime = Math.floor(Date.now() / 1000);

      const startDateTimeString = new Date(startDateTime * 1000).toLocaleString();
      const endDateTimeString = new Date(endDateTime * 1000).toLocaleString();
      const currentDateTimeString = new Date(currentDateTime * 1000).toLocaleString();

      // console.log("Start DateTime:", startDateTimeString);
      // console.log("End DateTime:", endDateTimeString);
      // console.log("Current DateTime:", currentDateTimeString);

      if (currentDateTime > startDateTime && currentDateTime < endDateTime) {
        console.log(true);
        setUpdateLimitReached(true);
        // console.log("setting updateLimitReached", true);
      } else {
        console.log(false);
        setUpdateLimitReached(false);
        // console.log("setting updateLimitReached", false);
      }

      const newEntry = {
        timestamp: getUnixTime(currentDate).toString(),
        date: currentDate.toISOString().split("T")[0],
      };

      weekData.push(newEntry);

      await set(ref(db, weekSubpath), weekData);

      setCurrentUpdates(weekData.length);
      setUpdateHistory([...updateHistory, newEntry]);
      // keep existing success toast for storage update as well
      handleToast("success", "Updated Successfully!");
    } catch (error) {
      handleToast("error", "Try again later!");
      console.error("Error updating data:", error.message);
    } finally {
      setLoading(false); // Stop loading spinner
    }
  };

  const handleCheckoutMode = async (mode) => { 
    if (mode === "book_now") { 
      // await updateValueInFBDB(`${user}/graph-account-info/checkoutLabel`, "Book Now Button");
      await updateDataInMongoDb(user, "graph-account-info",["checkoutLabel"], "Book Now Button");
      handleToast("success", "Checkout mode set to Book Now");
      // setCheckoutLabel
      dispatch(setCheckoutLabel("Book Now Button"));
    }
    else if (mode === "add_to_cart") {
      // await updateValueInFBDB(`${user}/graph-account-info/checkoutLabel`, "Add To Cart Button");
      await updateDataInMongoDb(user, "graph-account-info",["checkoutLabel"], "Add To Cart Button");  
      handleToast("success", "Checkout mode set to Add To Cart");
      dispatch(setCheckoutLabel("Add To Cart Button"));

    }
    else {
      handleToast("error", "Invalid checkout mode selected");
    }
  }

  // handleExportAllData moved to ExportClientReports.jsx

  return (
    <div className=" flex-grow m-3">
      <ToastContainer />
      {/* Page content always visible; show inline spinner inside Update button when loading */}
      <>
      <div className="bg-white flex-grow text-2xl text-gray-400 font-[700] mb-3">
        Settings
      </div>

      {/* Admin Detail Section */}
      <div className="mb-4 rounded-md border-2">
        <div className="pl-3 bg-gray-300 py-2 font-semibold">Admin Details</div>
        <div className="p-4">
          {adminLoading ? (
            <div className="flex justify-center items-center py-4">
              <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveAdminDetail();
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={adminForm.name}
                    onChange={handleAdminFormChange}
                    placeholder="Enter admin name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website URL
                  </label>
                  <input
                    type="url"
                    name="websiteUrl"
                    value={adminForm.websiteUrl}
                    onChange={handleAdminFormChange}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emails (comma-separated)
                </label>
                <input
                  type="text"
                  name="emails"
                  value={adminForm.emails}
                  onChange={handleAdminFormChange}
                  placeholder="admin@example.com, alerts@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Spreadsheet ID or URL
                </label>
                <input
                  type="text"
                  name="spreadsheetId"
                  value={adminForm.spreadsheetId}
                  onChange={handleAdminFormChange}
                  placeholder="1KVw... or https://docs.google.com/spreadsheets/d/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This spreadsheet will be shown on the dashboard.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Google Drive Blog Folder URL
                </label>
                <input
                  type="text"
                  name="blogFolderUrl"
                  value={adminForm.blogFolderUrl}
                  onChange={handleAdminFormChange}
                  placeholder="https://drive.google.com/drive/folders/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Shared folder where blog docs are stored for this client
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={adminSaving}
                  className={`px-4 py-2 rounded-md text-white font-semibold ${
                    adminSaving
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-600"
                  }`}
                >
                  {adminSaving ? "Saving..." : adminDetail ? "Update" : "Save"}
                </button>
                {adminDetail && (
                  <span className="text-sm text-green-600">
                    ✓ Admin details saved
                  </span>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

      {/* AI Agent Section — Admin Only */}
      {isAdmin && (
        <div className="mb-4 rounded-md border-2">
          <div className="pl-3 bg-gray-300 py-2 font-semibold">AI Agent Settings</div>
          <div className="p-4">
            {agentLoading ? (
              <div className="flex justify-center items-center py-4">
                <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-700">AI Agent for {user}</p>
                    <p className="text-sm text-gray-500">
                      {agentEnabled
                        ? "AI Insights, Chat, and InlineTips are visible for this client"
                        : "AI features are hidden for this client"}
                    </p>
                  </div>
                  <button
                    onClick={handleAgentToggle}
                    disabled={agentToggling}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                      agentEnabled ? "bg-emerald-500" : "bg-gray-300"
                    } ${agentToggling ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
                        agentEnabled ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                <div className={`flex items-center gap-2 text-sm ${agentEnabled ? "text-emerald-600" : "text-gray-400"}`}>
                  <span className={`w-2 h-2 rounded-full ${agentEnabled ? "bg-emerald-500" : "bg-gray-300"}`}></span>
                  {agentEnabled ? "Active" : "Inactive"}
                </div>

                {!agentEnabled && (
                  <p className="text-xs text-gray-400">
                    When disabled, the AI Insights drawer, Chat bubble, and page tips
                    will be completely hidden for this client.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mb-2 rounded-md border-2">
        <div className="pl-3 bg-gray-300 py-2">Settings</div>
        <div className="grid grid-cols-3 p-2">
          <div className="col-span-1">
            <p className="pl-3 p-1 font-semibold">Username</p>
            {/* <p className="pl-3 p-1 font-semibold">Persona Nudge</p> */}
            <p className="pl-3 p-1 font-semibold">Checkout Mode</p>
            <p className="pl-3 p-1 font-semibold">Dashboard Update</p>
          </div>
          <div className="col-span-2">
            <p className="pl-3 p-1 text-lg">{email}</p>

            {/* <p>
              <span className="mr-2 text-lg">
                {enable !== true ? "Disabled" : "Enabled"}
              </span>
              <button
                type="button"
                className="rounded-md bg-[#343A40] px-2.5 py-1 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                onClick={() => setEnable((prev) => !prev)}
              >
                {enable === true ? "Disable" : "Enable"}
              </button>
                </p> */}

            <p className="flex gap-2 items-center my-2">
              <button
                onClick={() => handleCheckoutMode("book_now")}
                className={
                  !UserAccountInfo?.checkoutLabel || UserAccountInfo?.checkoutLabel?.includes("Book Now")
                    ? "bg-emerald-600 text-white px-2 py-1 rounded"
                    : " text-gray-700 px-2 py-1 rounded hover:bg-gray-300 transition"
                }
              >
                Book Now
              </button>
              <button
                onClick={() => handleCheckoutMode("add_to_cart")}
                className={
                  UserAccountInfo?.checkoutLabel?.includes("Add To Cart")
                    ? "bg-emerald-600 text-white px-2 py-1 rounded"
                    : " text-gray-700 px-2 py-1 rounded hover:bg-gray-300 transition"
                }
              >
                Add To Cart
              </button>
            </p>

            <div className="flex items-center my-4">
              <div className="w-1/2 bg-white-200 rounded-full h-2.5 dark:bg-gray-700">
                {/* <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${progress}%` }}
                ></div> */}
              </div>
              {/* <span className="ml-3">{`(${currentUpdates}/3)`}</span> */}
            </div>

            <button
              disabled={updateLimitReached || loading} // Disable button when loading
              className={`inline ${updateLimitReached || loading
                ? "bg-gray-500"
                : "bg-blue-500 hover:bg-blue-700"
                } text-white font-bold py-2 px-4 rounded`}
              onClick={updateProgress}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Updating...
                </span>
              ) : (
                'Update'
              )}
            </button>
            {apiResponse && (
              <span className={`ml-3 ${apiResponse.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                {apiResponse.message}
              </span>
            )}
            {updateLimitReached && (
              <span className="ml-3 text-red-500">
                You've reached the maximum update limit for this 6-hour period.
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Export Client Reports Selection & Download Section */}
      <ExportClientReports user={user} />

      {/* Dashboard update history removed per request */}
      </>
      
    </div>
  );
};

export default Settings;